import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Avatar,
  Divider,
  Paper,
  Stack,
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CalendarMonth as CalendarIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import api from '../config/api';
import {
  StyledTableContainer,
  TableSkeleton,
  FilterBar,
  SearchInput,
  FilterSelect,
  StyledPagination,
  EmptyState,
  StatusChip,
  FormSkeleton,
} from '../components/TableShared';

const ff = "'Poppins', sans-serif";

const STATUS_OPTIONS = ['ALL', 'PENDING', 'APPROVED', 'REJECTED', 'SUSPENDED', 'INACTIVE'];

const STATUS_COLORS = {
  PENDING: 'warning',
  APPROVED: 'success',
  REJECTED: 'error',
  SUSPENDED: 'default',
  INACTIVE: 'default',
};

const STATUS_CHIP_MAP = {
  PENDING: { color: '#D69E2E', bgColor: '#FFFFF0' },
  APPROVED: { color: '#38A169', bgColor: '#F0FFF4' },
  REJECTED: { color: '#E53E3E', bgColor: '#FFF5F5' },
  SUSPENDED: { color: '#718096', bgColor: '#F7FAFC' },
  INACTIVE: { color: '#A0AEC0', bgColor: '#F7FAFC' },
};

export default function Sitters() {
  const [sitters, setSitters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalSitters, setTotalSitters] = useState(0);

  // Filters
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // Confirmation dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogAction, setDialogAction] = useState('');
  const [selectedSitter, setSelectedSitter] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');

  // Detail dialog state
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailSitter, setDetailSitter] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchSitters = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params = { page: page + 1, limit: rowsPerPage };
      if (statusFilter !== 'ALL') {
        params.status = statusFilter;
      }
      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }
      const response = await api.get('/admin/sitters', { params });
      const data = response.data.data || response.data;
      setSitters(data.sitters || []);
      setTotalSitters(data.total || data.sitters?.length || 0);
    } catch (err) {
      console.error('Error fetching sitters:', err);
      setError('Failed to load sitters. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, statusFilter, searchQuery]);

  useEffect(() => {
    fetchSitters();
  }, [fetchSitters]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput);
      setPage(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setPage(0);
  };

  const handleActionClick = (sitter, action) => {
    setSelectedSitter(sitter);
    setDialogAction(action);
    setRejectionReason('');
    setDialogOpen(true);
  };

  const handleActionConfirm = async () => {
    if (!selectedSitter) return;
    if (dialogAction === 'reject' && !rejectionReason.trim()) return;
    try {
      setActionLoading(true);
      if (dialogAction === 'approve') {
        await api.put(`/admin/sitters/${selectedSitter.id}/approve`);
      } else {
        await api.put(`/admin/sitters/${selectedSitter.id}/reject`, {
          rejectionReason: rejectionReason.trim()
        });
      }
      setDialogOpen(false);
      setSelectedSitter(null);
      setRejectionReason('');
      fetchSitters();
    } catch (err) {
      console.error(`Error ${dialogAction}ing sitter:`, err);
      setError(`Failed to ${dialogAction} sitter. Please try again.`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedSitter(null);
    setRejectionReason('');
  };

  const handleDetailClick = async (sitter) => {
    setDetailSitter(sitter);
    setDetailOpen(true);
    try {
      setDetailLoading(true);
      const response = await api.get(`/admin/sitters/${sitter.id}`);
      const data = response.data.data || response.data;
      setDetailSitter(data.sitter || data || sitter);
    } catch (err) {
      // If the detail endpoint fails, just show what we already have
      console.error('Error fetching sitter details:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDetailClose = () => {
    setDetailOpen(false);
    setDetailSitter(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch {
      return 'N/A';
    }
  };

  const StatBox = ({ label, value }) => (
    <Box sx={{ textAlign: 'center', px: 1.5, py: 1 }}>
      <Typography variant="h6" fontWeight={700} color="primary.main" sx={{ fontFamily: ff }}>
        {value ?? 'N/A'}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: ff }}>
        {label}
      </Typography>
    </Box>
  );

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Filters Bar */}
      <FilterBar>
        <SearchInput
          placeholder="Search by name or email..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        <FilterSelect
          label="Status"
          value={statusFilter}
          onChange={handleStatusFilterChange}
          options={STATUS_OPTIONS.map((s) => ({
            value: s,
            label: s === 'ALL' ? 'All Statuses' : s,
          }))}
          minWidth={160}
        />
      </FilterBar>

      {loading ? (
        <TableSkeleton columns={6} rows={6} />
      ) : (
        <>
          <StyledTableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Services</TableCell>
                  <TableCell>Joined</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sitters.map((sitter) => (
                  <TableRow key={sitter.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar
                          src={sitter.avatar || sitter.profileImage}
                          alt={sitter.fullName}
                          sx={{ width: 34, height: 34, bgcolor: '#005A64', fontSize: 14 }}
                        >
                          {sitter.fullName ? sitter.fullName.charAt(0).toUpperCase() : '?'}
                        </Avatar>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: ff,
                            cursor: 'pointer',
                            fontWeight: 500,
                            '&:hover': { color: 'primary.main', textDecoration: 'underline' }
                          }}
                          onClick={() => handleDetailClick(sitter)}
                        >
                          {sitter.fullName || 'N/A'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{sitter.email || 'N/A'}</TableCell>
                    <TableCell>
                      <StatusChip
                        label={sitter.approvalStatus || 'PENDING'}
                        color={STATUS_CHIP_MAP[sitter.approvalStatus]?.color || '#718096'}
                        bgColor={STATUS_CHIP_MAP[sitter.approvalStatus]?.bgColor || '#F7FAFC'}
                      />
                    </TableCell>
                    <TableCell>
                      {sitter.serviceCount != null ? sitter.serviceCount : 'N/A'}
                    </TableCell>
                    <TableCell>
                      {formatDate(sitter.createdAt)}
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        color="success"
                        startIcon={<ApproveIcon />}
                        onClick={() => handleActionClick(sitter, 'approve')}
                        disabled={sitter.approvalStatus === 'APPROVED'}
                        sx={{ mr: 1, fontFamily: ff, fontSize: 12 }}
                      >
                        Approve
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        startIcon={<RejectIcon />}
                        onClick={() => handleActionClick(sitter, 'reject')}
                        disabled={sitter.approvalStatus === 'REJECTED'}
                        sx={{ fontFamily: ff, fontSize: 12 }}
                      >
                        Reject
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {sitters.length === 0 && (
                  <EmptyState colSpan={6} message="No sitters found." />
                )}
              </TableBody>
            </Table>
            <StyledPagination
              count={totalSitters}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </StyledTableContainer>
        </>
      )}

      {/* Approve / Reject Confirmation Dialog */}
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: ff }}>
          {dialogAction === 'approve' ? 'Approve' : 'Reject'} Sitter
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: dialogAction === 'reject' ? 2 : 0, fontFamily: ff }}>
            Are you sure you want to {dialogAction}{' '}
            <strong>
              {selectedSitter
                ? selectedSitter.fullName || selectedSitter.email
                : ''}
            </strong>
            ?
          </DialogContentText>
          {dialogAction === 'reject' && (
            <TextField
              autoFocus
              fullWidth
              multiline
              minRows={3}
              maxRows={6}
              label="Rejection Reason *"
              placeholder="Provide a reason for rejection (required)..."
              error={dialogAction === 'reject' && rejectionReason.trim() === ''}
              helperText={dialogAction === 'reject' && rejectionReason.trim() === '' ? 'Rejection reason is required' : ''}
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              sx={{ mt: 1, '& .MuiInputBase-root': { fontFamily: ff } }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} disabled={actionLoading} sx={{ fontFamily: ff }}>
            Cancel
          </Button>
          <Button
            onClick={handleActionConfirm}
            color={dialogAction === 'approve' ? 'success' : 'error'}
            variant="contained"
            disabled={actionLoading || (dialogAction === 'reject' && !rejectionReason.trim())}
            sx={{ fontFamily: ff }}
          >
            {actionLoading ? 'Processing...' : dialogAction === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sitter Detail Dialog */}
      <Dialog open={detailOpen} onClose={handleDetailClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 0, fontFamily: ff }}>Sitter Details</DialogTitle>
        <DialogContent>
          {detailLoading ? (
            <FormSkeleton />
          ) : detailSitter ? (
            <Box sx={{ pt: 2 }}>
              {/* Profile Header */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Avatar
                  src={detailSitter.avatar || detailSitter.profileImage}
                  alt={detailSitter.fullName}
                  sx={{ width: 72, height: 72, bgcolor: '#005A64', fontSize: 28 }}
                >
                  {detailSitter.fullName ? detailSitter.fullName.charAt(0).toUpperCase() : '?'}
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight={600} sx={{ fontFamily: ff }}>
                    {detailSitter.fullName || 'N/A'}
                  </Typography>
                  <Chip
                    label={detailSitter.approvalStatus || 'PENDING'}
                    color={STATUS_COLORS[detailSitter.approvalStatus] || 'default'}
                    size="small"
                  />
                </Box>
              </Box>

              <Divider sx={{ mb: 2 }} />

              {/* Contact Info */}
              <Stack spacing={1} sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EmailIcon fontSize="small" color="action" />
                  <Typography variant="body2" sx={{ fontFamily: ff }}>{detailSitter.email || 'N/A'}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PhoneIcon fontSize="small" color="action" />
                  <Typography variant="body2" sx={{ fontFamily: ff }}>{detailSitter.phone || detailSitter.phoneNumber || 'N/A'}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarIcon fontSize="small" color="action" />
                  <Typography variant="body2" sx={{ fontFamily: ff }}>
                    Registered: {formatDate(detailSitter.createdAt)}
                  </Typography>
                </Box>
              </Stack>

              {/* Approval / Rejection dates */}
              {detailSitter.approvedAt && (
                <Typography variant="body2" color="success.main" sx={{ mb: 0.5, fontFamily: ff }}>
                  Approved on: {formatDate(detailSitter.approvedAt)}
                </Typography>
              )}
              {detailSitter.rejectedAt && (
                <Typography variant="body2" color="error.main" sx={{ mb: 0.5, fontFamily: ff }}>
                  Rejected on: {formatDate(detailSitter.rejectedAt)}
                </Typography>
              )}
              {detailSitter.rejectionReason && (
                <Alert severity="error" variant="outlined" sx={{ mb: 2, mt: 1 }}>
                  <Typography variant="body2" sx={{ fontFamily: ff }}>
                    <strong>Rejection reason:</strong> {detailSitter.rejectionReason}
                  </Typography>
                </Alert>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Stats */}
              <Typography variant="subtitle2" gutterBottom fontWeight={600} sx={{ fontFamily: ff }}>
                Performance Stats
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'space-around',
                  py: 1,
                  mb: 2,
                }}
              >
                <StatBox label="Bookings" value={detailSitter.totalBookings ?? detailSitter.bookingCount} />
                <StatBox label="Completed" value={detailSitter.completedBookings ?? detailSitter.completedCount} />
                <StatBox label="Cancelled" value={detailSitter.cancelledBookings ?? detailSitter.cancelledCount} />
                <StatBox label="Avg Rating" value={
                  detailSitter.averageRating != null
                    ? Number(detailSitter.averageRating).toFixed(1)
                    : detailSitter.avgRating != null
                      ? Number(detailSitter.avgRating).toFixed(1)
                      : 'N/A'
                } />
                <StatBox label="Reviews" value={detailSitter.totalReviews ?? detailSitter.reviewCount} />
                <StatBox label="Services" value={detailSitter.serviceCount} />
              </Paper>

              {/* Bio */}
              {(detailSitter.bio || detailSitter.description) && (
                <>
                  <Typography variant="subtitle2" gutterBottom fontWeight={600} sx={{ fontFamily: ff }}>
                    Bio
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', fontFamily: ff }}>
                    {detailSitter.bio || detailSitter.description}
                  </Typography>
                </>
              )}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDetailClose} sx={{ fontFamily: ff }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
